import { FC, MouseEvent } from 'react';
import { cx } from '@emotion/css';
import { useTranslation } from 'react-i18next';
import { DSLink } from '@stihl-design-system/components';

import { getDesignSystemIcon } from '@phoenix/design-system/frontend/util/path.utils';
import { PhoenixIcon } from '@phoenix/design-system/frontend/ui/phoenix-icon/phoenix-icon';

import { EditorialCardImage, EditorialCardImageProps } from './editorial-card-image';

export interface EditorialCardProps {
  image?: EditorialCardImageProps;
  tag?: string;
  title: string;
  isLinkOut?: boolean;
  description?: string;
  icons?: { name: string; text?: string }[];
  additionalInfo?: string[];
  labelTag?: string;
  onClick?: (e: MouseEvent<HTMLElement>) => void;
  className?: string;
  dataTestId?: string;
  linkOutText?: string;
}

export const EditorialCard: FC<React.PropsWithChildren<EditorialCardProps>> = ({
  image,
  tag,
  title,
  isLinkOut,
  description,
  icons,
  additionalInfo,
  labelTag,
  onClick,
  className,
  dataTestId,
  linkOutText,
}) => {
  const { t } = useTranslation('shared');

  return (
    <article
      aria-label={title}
      className={cx(
        'relative flex max-w-[32rem] flex-1 flex-col overflow-hidden rounded-lg border border-secondary-mid bg-primary-white',
        onClick && 'cursor-pointer',
        className
      )}
      onClick={onClick}
      data-testid={dataTestId}
    >
      {image && (
        <div className="relative">
          <EditorialCardImage {...image} className={cx('aspect-[16/9]', image.className)} />
          {labelTag !== undefined && (
            <span
              className={cx(
                'absolute right-4 top-4 h-6 max-w-fit truncate px-2 py-1 !text-xs text-primary-white stihl-text-highlight-flag',
                labelTag && 'bg-primary-orange'
              )}
            >
              {labelTag}
            </span>
          )}
        </div>
      )}
      <div className="relative flex flex-1 flex-col justify-between gap-y-6 p-6">
        {isLinkOut && (
          <span className="absolute right-6 top-6">
            <PhoenixIcon className="h-4 w-4" icon={getDesignSystemIcon('icon-external-link-16.svg')} />
          </span>
        )}
        <div className="space-y-4">
          <div className={cx({ 'pr-6': isLinkOut })}>
            {tag !== undefined && <span className="block h-8 truncate pb-2 text-primary-orange stihl-text-highlight-flag">{tag}</span>}
            <h4 title={title} className="line-clamp-2 stihl-text-h5-desktop">
              {title}
            </h4>
          </div>
          {description !== undefined && <div className="line-clamp-3 max-h-[4.5rem] flex-1 stihl-text-body-default-reg">{description}</div>}
          {icons?.length && (
            <div className="space-y-2">
              {icons.map((icon, index) => (
                <div key={icon.text} className="flex items-center gap-2">
                  <PhoenixIcon className="h-4 w-4 text-primary-black" icon={getDesignSystemIcon(icon.name)} />
                  {icon.text && (
                    <span className={cx(index === 0 ? 'stihl-text-body-default-bold' : 'stihl-text-body-default-reg')}>{icon.text}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center justify-between">
          {linkOutText && (
            <DSLink active iconName="arrow-out" iconPosition="right">
              {linkOutText}
              <span className="sr-only">({t('shared_a11y_hint_opensInNewTab')})</span>
            </DSLink>
          )}
          {additionalInfo !== undefined && (
            <div className="h-5">
              {additionalInfo.map((info, i) => (
                <span key={`editorial-card-info-${i}`} className="mr-4 stihl-text-body-sm-reg last:mr-0">
                  {info}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </article>
  );
};
